import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import TargetAudienceSelector from '../components/TargetAudienceSelector';

const StaffAnnouncements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [readReceipts, setReadReceipts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [category, setCategory] = useState('General');
  const [targetType, setTargetType] = useState('all');
  const [targetRoles, setTargetRoles] = useState([]);
  const [targetSquads, setTargetSquads] = useState([]);
  const [targetAthletes, setTargetAthletes] = useState([]);
  const [targetTagIds, setTargetTagIds] = useState([]);
  const [isPinned, setIsPinned] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const [tags, setTags] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
    fetchAthletes();
    fetchSquads();
    fetchTags();
  }, []);

  // Deep-link support — e.g. /announcements?targetSquad=U19 opens the dialog pre-targeted
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sq = searchParams.get('targetSquad');
    if (sq && !createDialogOpen) {
      setTargetType('athletes');
      setTargetSquads([sq]);
      setCreateDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements?include_expired=true`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/athletes?status=active`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (r.ok) setTags(await r.json());
    } catch (_) { /* silent */ }
  };

  const previewAudience = async () => {
    setPreviewBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/preview-audience`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_type: targetType,
          target_roles: targetType === 'role' ? targetRoles : null,
          target_squads: targetSquads.length ? targetSquads : null,
          target_athlete_ids: targetAthletes.length ? targetAthletes : null,
          target_tag_ids: targetTagIds.length ? targetTagIds : null,
        }),
      });
      if (r.ok) setAudiencePreview(await r.json());
      else toast.error('Preview failed');
    } catch (_) { toast.error('Preview failed'); }
    finally { setPreviewBusy(false); }
  };

  const fetchSquads = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/squads`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSquads(data.squads || []);
      }
    } catch (error) {
      console.error('Error fetching squads:', error);
    }
  };

  const handleAttachmentUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/upload-attachment`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAttachmentUrl(data.attachment_url);
        setAttachmentName(data.attachment_name);
        toast.success('Attachment uploaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload attachment');
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      toast.error('Failed to upload attachment');
    }
  };

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setPriority('Normal');
    setCategory('General');
    setTargetType('all');
    setTargetRoles([]);
    setTargetSquads([]);
    setTargetAthletes([]);
    setTargetTagIds([]);
    setIsPinned(false);
    setExpiresAt('');
    setAttachment(null);
    setAttachmentUrl('');
    setAttachmentName('');
    setAudiencePreview(null);
  };

  const handleCreate = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    // Validate targeting
    if (targetType === 'role' && targetRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    if (targetType === 'athletes' && targetSquads.length === 0 && targetAthletes.length === 0 && targetTagIds.length === 0) {
      toast.error('Please select squads, tags, or athletes');
      return;
    }

    setSubmitting(true);

    // Map TargetAudienceSelector output to announcement backend format
    let announcementTargetType = targetType;
    let announcementSquads = null;
    let announcementAthleteIds = null;
    
    if (targetType === 'athletes') {
      if (targetAthletes.length > 0 && targetSquads.length === 0) {
        announcementTargetType = 'athletes';
        announcementAthleteIds = targetAthletes;
      } else if (targetSquads.length > 0 && targetAthletes.length === 0) {
        announcementTargetType = 'squad';
        announcementSquads = targetSquads;
      } else if (targetSquads.length > 0 && targetAthletes.length > 0) {
        // Mixed: send both as athletes type with squad athletes resolved on frontend
        announcementTargetType = 'athletes';
        announcementAthleteIds = targetAthletes;
        announcementSquads = targetSquads;
      }
    }

    const payload = {
      title,
      message,
      priority,
      category,
      target_type: announcementTargetType,
      target_roles: targetType === 'role' ? targetRoles : null,
      target_squads: announcementSquads,
      target_athlete_ids: announcementAthleteIds,
      target_tag_ids: targetTagIds.length ? targetTagIds : null,
      is_pinned: isPinned,
      expires_at: expiresAt || null,
      attachment_url: attachmentUrl || null,
      attachment_name: attachmentName || null
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Announcement created successfully');
        setCreateDialogOpen(false);
        resetForm();
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create announcement');
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      toast.error('Failed to create announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete announcement');
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const handleViewDetails = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setViewDialogOpen(true);

    // Fetch read receipts
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcement.id}/read-receipts`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReadReceipts(data);
      }
    } catch (error) {
      console.error('Error fetching read receipts:', error);
    }
  };

  const togglePin = async (announcement) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcement.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_pinned: !announcement.is_pinned })
        }
      );

      if (response.ok) {
        toast.success(announcement.is_pinned ? 'Unpinned' : 'Pinned');
        fetchAnnouncements();
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Normal': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700',
      'Important': 'bg-amber-100 text-amber-800 border-amber-200',
      'Urgent': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority] || colors['Normal'];
  };

  const getTargetLabel = (announcement) => {
    switch (announcement.target_type) {
      case 'all': return 'All Users';
      case 'role': return `Roles: ${announcement.target_roles?.join(', ')}`;
      case 'squad': return `Squads: ${announcement.target_squads?.join(', ')}`;
      case 'athletes': return `${announcement.target_athlete_ids?.length} Athletes`;
      default: return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const toggleRole = (role) => {
    setTargetRoles(prev => 
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const toggleSquad = (squad) => {
    setTargetSquads(prev => 
      prev.includes(squad) ? prev.filter(s => s !== squad) : [...prev, squad]
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Announcements
            </h2>
            <p className="text-gray-600 dark:text-gray-500">Create and manage announcements for your athletes</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-pfap-green hover:bg-pfap-green/90" data-testid="create-announcement-btn">
                + Create Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Announcement</DialogTitle>
                <DialogDescription>
                  Send a new announcement to your audience
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Announcement title"
                    data-testid="announcement-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write your announcement..."
                    rows={4}
                    data-testid="announcement-message-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Priority</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Important">Important</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Training">Training</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Target Audience</Label>
                    <Select value={targetType} onValueChange={setTargetType}>
                      <SelectTrigger data-testid="target-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(user?.role === 'Admin' || user?.role === 'Staff') && (
                          <>
                            <SelectItem value="all">All Users</SelectItem>
                            <SelectItem value="role">By Role</SelectItem>
                          </>
                        )}
                        <SelectItem value="athletes">By Athletes / Squads / Tags</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Role Selection */}
                {targetType === 'role' && (
                  <div>
                    <Label>Select Roles</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['Admin', 'Staff', 'Player', 'Parent'].map(role => (
                        <Button
                          key={role}
                          type="button"
                          variant={targetRoles.includes(role) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleRole(role)}
                        >
                          {role}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Squad Selection - now handled by TargetAudienceSelector */}

                {/* Athlete / Squad / Tag Selection via TargetAudienceSelector */}
                {targetType === 'athletes' && (
                  <>
                    <TargetAudienceSelector
                      athletes={athletes}
                      tags={tags}
                      value={{
                        target_type: (targetSquads.length > 0 || targetAthletes.length > 0 || targetTagIds.length > 0) ? 'custom' : 'all',
                        target_squads: targetSquads,
                        target_athletes: targetAthletes,
                        target_tag_ids: targetTagIds,
                      }}
                      onChange={(val) => {
                        setTargetSquads(val.target_squads || []);
                        setTargetAthletes(val.target_athletes || []);
                        setTargetTagIds(val.target_tag_ids || []);
                        setAudiencePreview(null);
                      }}
                      label="Select Target Athletes"
                      data-testid="announcement-target-audience"
                    />
                    <div className="flex items-center justify-between pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={previewAudience}
                        disabled={previewBusy}
                        data-testid="preview-audience-btn"
                      >
                        {previewBusy ? 'Resolving…' : 'Preview audience'}
                      </Button>
                      {audiencePreview && (
                        <Badge
                          variant="outline"
                          className="text-[11px]"
                          data-testid="preview-audience-count"
                          title={audiencePreview.preview?.slice(0, 10).map(a => a.full_name).join(', ')}
                        >
                          {audiencePreview.athlete_count} active athlete{audiencePreview.athlete_count !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    {audiencePreview && audiencePreview.preview && audiencePreview.preview.length > 0 && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 rounded-md p-2 max-h-24 overflow-y-auto" data-testid="preview-audience-list">
                        {audiencePreview.preview.slice(0, 12).map(a => a.full_name).join(' · ')}
                        {audiencePreview.athlete_count > 12 && ` · +${audiencePreview.athlete_count - 12} more`}
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expires">Expires (Optional)</Label>
                    <Input
                      id="expires"
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <Checkbox checked={isPinned} onCheckedChange={setIsPinned} />
                      <span>Pin this announcement</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label htmlFor="attachment">Attachment (Optional)</Label>
                  <Input
                    id="attachment"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setAttachment(file);
                        handleAttachmentUpload(file);
                      }
                    }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.xls"
                  />
                  {attachmentName && (
                    <p className="text-sm text-emerald-600 mt-1">Uploaded: {attachmentName}</p>
                  )}
                </div>

                <Button
                  onClick={handleCreate}
                  disabled={submitting}
                  className="w-full bg-pfap-green hover:bg-pfap-green/90"
                  data-testid="submit-announcement-btn"
                >
                  {submitting ? 'Creating...' : 'Create Announcement'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Announcements List */}
        {announcements.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-500 dark:text-gray-500">No announcements yet. Create your first one!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {announcements.map(announcement => (
              <Card 
                key={announcement.id} 
                className={announcement.is_pinned ? 'border-emerald-300 bg-emerald-50/30' : ''}
                data-testid={`announcement-${announcement.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {announcement.is_pinned && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            📌 Pinned
                          </Badge>
                        )}
                        <h4 className="font-semibold text-lg">{announcement.title}</h4>
                        <Badge className={getPriorityColor(announcement.priority)}>
                          {announcement.priority}
                        </Badge>
                        {announcement.category && announcement.category !== 'General' && (
                          <Badge
                            className={
                              announcement.category === 'Training'
                                ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                                : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                            }
                            data-testid={`category-badge-${announcement.id}`}
                          >
                            {announcement.category}
                          </Badge>
                        )}
                        {isExpired(announcement.expires_at) && (
                          <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500">Expired</Badge>
                        )}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mb-3 whitespace-pre-wrap">{announcement.message}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-500">
                        <span>📢 {getTargetLabel(announcement)}</span>
                        <span>👤 {announcement.created_by_name}</span>
                        <span>📅 {formatDate(announcement.created_at)}</span>
                        {announcement.expires_at && (
                          <span>⏰ Expires: {formatDate(announcement.expires_at)}</span>
                        )}
                        {announcement.attachment_name && (
                          <span>📎 {announcement.attachment_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDetails(announcement)}
                        data-testid={`view-announcement-${announcement.id}`}
                      >
                        Details
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => togglePin(announcement)}
                      >
                        {announcement.is_pinned ? 'Unpin' : 'Pin'}
                      </Button>
                      {(user?.role === 'Admin' || announcement.created_by === user?.id) && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(announcement.id)}
                          data-testid={`delete-announcement-${announcement.id}`}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedAnnouncement?.title}</DialogTitle>
            </DialogHeader>
            {selectedAnnouncement && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge className={getPriorityColor(selectedAnnouncement.priority)}>
                    {selectedAnnouncement.priority}
                  </Badge>
                  <Badge variant="outline">{getTargetLabel(selectedAnnouncement)}</Badge>
                </div>
                <p className="whitespace-pre-wrap">{selectedAnnouncement.message}</p>
                
                {selectedAnnouncement.attachment_url && (
                  <div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${selectedAnnouncement.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:underline"
                    >
                      📎 Download: {selectedAnnouncement.attachment_name}
                    </a>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Read Receipts ({readReceipts.length})</h4>
                  {readReceipts.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-500">No one has read this announcement yet</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {readReceipts.map(receipt => (
                        <div key={receipt.id} className="text-sm flex justify-between">
                          <span>{receipt.user_name}</span>
                          <span className="text-gray-500 dark:text-gray-500">{formatDate(receipt.read_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StaffAnnouncements;
